import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UserItem } from '@/types/userItem';
import type { CollectionSortOption } from '@/components/collection/CollectionSortButton';
import { matchesStatusBucket, type StatusBucket } from './inventoryHelpers';

export type VisibilityToggle = 'all' | 'listed' | 'unlisted';

export interface AvailableFilterOptions {
  availableGames: string[];
  availableCategories: string[];
  availableConditions: string[];
  availableLanguages: string[];
  availableGradingCompanies: string[];
  availableProductTypes: string[];
  hasSlabs: boolean;
}

export interface UseInventoryFiltersResult {
  // state
  activeTab: StatusBucket[];
  setActiveTab: React.Dispatch<React.SetStateAction<StatusBucket[]>>;
  categoryFilter: string[];
  setCategoryFilter: React.Dispatch<React.SetStateAction<string[]>>;
  conditionFilter: string;
  setConditionFilter: React.Dispatch<React.SetStateAction<string>>;
  priceMin: number;
  setPriceMin: React.Dispatch<React.SetStateAction<number>>;
  priceMax: number;
  setPriceMax: React.Dispatch<React.SetStateAction<number>>;
  selectedGame: string;
  setSelectedGame: React.Dispatch<React.SetStateAction<string>>;
  selectedLanguages: string[];
  setSelectedLanguages: React.Dispatch<React.SetStateAction<string[]>>;
  gradingCompany: string;
  setGradingCompany: React.Dispatch<React.SetStateAction<string>>;
  gradeMin: number;
  setGradeMin: React.Dispatch<React.SetStateAction<number>>;
  gradeMax: number;
  setGradeMax: React.Dispatch<React.SetStateAction<number>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  sortBy: CollectionSortOption;
  setSortBy: React.Dispatch<React.SetStateAction<CollectionSortOption>>;
  // derived
  filteredItems: UserItem[];
  publicListedIds: string[];
  unlistedRawItems: UserItem[];
  unlistedFilteredItems: UserItem[];
  stats: { total: number; listed: number; private: number; sold: number };
  itemsWithoutImagesCount: number;
  pricedItemsCount: number;
  mobileFilterCount: number;
  visibilityToggle: VisibilityToggle;
  setVisibilityToggle: (value: VisibilityToggle) => void;
  availableFilterOptions: AvailableFilterOptions;
  // actions
  clearFilters: () => void;
}

export function useInventoryFilters(
  myItemsRaw: UserItem[],
  freezeOrder: boolean = false,
  options: { includeSold?: boolean } = {},
): UseInventoryFiltersResult {
  const [activeTab, setActiveTab] = useState<StatusBucket[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [conditionFilter, setConditionFilter] = useState('all');
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(1000);
  const [selectedGame, setSelectedGame] = useState('all');
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [gradingCompany, setGradingCompany] = useState('all');
  const [gradeMin, setGradeMin] = useState(1);
  const [gradeMax, setGradeMax] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<CollectionSortOption>('newest');

  const filteredItems = useMemo(() => {
    const result = myItemsRaw.filter((item: UserItem) => {
      const isSold = !!item.sold_at;
      const statusWantsSold = activeTab.includes('sold');
      if (isSold && !options.includeSold && !statusWantsSold) return false;
      if (activeTab.length > 0 && !activeTab.some(b => matchesStatusBucket(item, b))) return false;
      if (categoryFilter.length > 0 && !categoryFilter.includes(item.category || '')) return false;
      if (conditionFilter !== 'all' && item.condition !== conditionFilter) return false;

      if (item.asking_price_cents) {
        const priceInDollars = item.asking_price_cents / 100;
        if (priceInDollars < priceMin) return false;
        if (priceMax < 1000 && priceInDollars > priceMax) return false;
      }

      if (selectedGame !== 'all') {
        const itemGame = item.game || 'other';
        if (itemGame !== selectedGame) return false;
      }

      if (selectedLanguages.length > 0 && item.language && !selectedLanguages.includes(item.language)) return false;

      // Grading filters implicitly scope to slabs even when category === 'all'.
      if (gradingCompany !== 'all') {
        if (item.category !== 'slabs' || item.grading_company !== gradingCompany) return false;
      }
      if (gradeMin !== 1 || gradeMax !== 10) {
        if (item.category !== 'slabs' || item.slab_grade == null) return false;
        if (item.slab_grade < gradeMin || item.slab_grade > gradeMax) return false;
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return item.title.toLowerCase().includes(query);
      }

      return true;
    });

    // H3: precompute created_at timestamps once (O(n)) instead of parsing
    // twice per comparison (O(n log n)). At 1k items that's ~14k Date allocs avoided per keystroke.
    const tsCache = new Map<string, number>();
    const ts = (it: typeof result[number]) => {
      let v = tsCache.get(it.id);
      if (v === undefined) { v = Date.parse(it.created_at); tsCache.set(it.id, v); }
      return v;
    };
    result.sort((a, b) => {
      switch (sortBy) {
        case 'priced_first': {
          const bucket = (it: typeof a) => {
            if (it.sold_at) return 4;
            if (it.visibility !== 'listed') return 0;
            if ((it.asking_price_cents ?? 0) > 0) return 1;
            if (it.is_negotiable) return 2;
            return 3;
          };
          const aB = bucket(a);
          const bB = bucket(b);
          if (aB !== bB) return aB - bB;
          if (aB === 1) return (b.asking_price_cents || 0) - (a.asking_price_cents || 0);
          return ts(b) - ts(a);
        }
        case 'oldest':
          return ts(a) - ts(b);
        case 'price_asc':
          return (a.asking_price_cents || 0) - (b.asking_price_cents || 0);
        case 'price_desc':
          return (b.asking_price_cents || 0) - (a.asking_price_cents || 0);
        case 'title_asc':
          return a.title.localeCompare(b.title);
        case 'no_images': {
          const aHasImages = !!a.images?.[0]?.image_url || !!a.stock_image_url;
          const bHasImages = !!b.images?.[0]?.image_url || !!b.stock_image_url;
          if (aHasImages === bHasImages) return ts(b) - ts(a);
          return aHasImages ? 1 : -1;
        }
        case 'high_quantity': {
          if (a.quantity !== b.quantity) return b.quantity - a.quantity;
          return ts(b) - ts(a);
        }
        case 'newest':
        default:
          return ts(b) - ts(a);
      }
    });

    return result;
  }, [myItemsRaw, activeTab, categoryFilter, conditionFilter, priceMin, priceMax, selectedGame, selectedLanguages, gradingCompany, gradeMin, gradeMax, searchQuery, sortBy, options.includeSold]);

  // Freeze display order while selection mode is active so that bulk actions
  // (pricing, listing, etc.) don't yank just-actioned items to a new position
  // and break follow-up actions.
  const frozenOrderRef = useRef<string[] | null>(null);
  useEffect(() => {
    if (!freezeOrder) frozenOrderRef.current = null;
  }, [freezeOrder]);
  if (freezeOrder && frozenOrderRef.current === null) {
    frozenOrderRef.current = filteredItems.map((it) => it.id);
  }

  const displayItems = useMemo(() => {
    if (!freezeOrder || !frozenOrderRef.current) return filteredItems;
    const order = frozenOrderRef.current;
    const rank = new Map<string, number>();
    order.forEach((id, i) => rank.set(id, i));
    // Keep items ordered by the frozen snapshot; append any new items at the end.
    return [...filteredItems].sort((a, b) => {
      const ra = rank.has(a.id) ? rank.get(a.id)! : Number.MAX_SAFE_INTEGER;
      const rb = rank.has(b.id) ? rank.get(b.id)! : Number.MAX_SAFE_INTEGER;
      return ra - rb;
    });
  }, [filteredItems, freezeOrder]);


  const stats = useMemo(() => {
    const active = myItemsRaw.filter((i: UserItem) => !i.sold_at);
    return {
      total: active.length,
      listed: active.filter((i: UserItem) => i.visibility === 'listed').length,
      private: active.filter((i: UserItem) => i.visibility === 'private').length,
      sold: myItemsRaw.filter((i: UserItem) => !!i.sold_at).length,
    };
  }, [myItemsRaw]);

  const publicListedIds = useMemo(
    () => myItemsRaw.filter((i: UserItem) => !i.sold_at && i.visibility === 'listed').map((i: UserItem) => i.id),
    [myItemsRaw],
  );

  const unlistedRawItems = useMemo(
    () => myItemsRaw.filter((i: UserItem) => !i.sold_at && i.visibility !== 'listed'),
    [myItemsRaw],
  );

  const unlistedFilteredItems = useMemo(
    () => displayItems.filter((i: UserItem) => !i.sold_at && i.visibility !== 'listed'),
    [displayItems],
  );

  const itemsWithoutImagesCount = useMemo(
    () => filteredItems.filter(item => !item.images?.[0]?.image_url && !item.stock_image_url).length,
    [filteredItems],
  );

  const pricedItemsCount = useMemo(
    () => filteredItems.filter(item => item.asking_price_cents && item.asking_price_cents > 0).length,
    [filteredItems],
  );

  const mobileFilterCount = useMemo(() => {
    let n = 0;
    n += activeTab.length;
    if (categoryFilter.length > 0) n++;
    if (conditionFilter !== 'all') n++;
    if (priceMin > 0 || priceMax < 1000) n++;
    if (selectedGame !== 'all') n++;
    if (selectedLanguages.length > 0) n++;
    if (gradingCompany !== 'all') n++;
    if (gradeMin !== 1 || gradeMax !== 10) n++;
    return n;
  }, [activeTab, categoryFilter, conditionFilter, priceMin, priceMax, selectedGame, selectedLanguages, gradingCompany, gradeMin, gradeMax]);

  // Quick visibility toggle derived from activeTab (All / Listed / Unlisted).
  const visibilityToggle: VisibilityToggle = useMemo(() => {
    const hasListed = activeTab.includes('listed');
    const hasPrivate = activeTab.includes('private');
    if (hasListed && !hasPrivate) return 'listed';
    if (hasPrivate && !hasListed) return 'unlisted';
    return 'all';
  }, [activeTab]);
  const setVisibilityToggle = useCallback((value: VisibilityToggle) => {
    // Preserve non-visibility buckets (priced / taking_offers) when toggling.
    setActiveTab(prev => {
      const preserved = prev.filter(b => b !== 'listed' && b !== 'private');
      if (value === 'all') return preserved;
      if (value === 'listed') return [...preserved, 'listed'];
      return [...preserved, 'private'];
    });
  }, []);

  // Derive available filter values from the seller's actual inventory so the
  // filter tray never lists games/categories/etc. they don't own.
  const availableFilterOptions = useMemo<AvailableFilterOptions>(() => {
    const sourceItems = options.includeSold ? myItemsRaw : myItemsRaw.filter((item) => !item.sold_at);
    const games = new Set<string>();
    const categories = new Set<string>();
    const conditions = new Set<string>();
    const languages = new Set<string>();
    const gradingCompanies = new Set<string>();
    const productTypes = new Set<string>();
    let hasSlabs = false;

    for (const item of sourceItems) {
      if (item.game) games.add(item.game);
      if (item.category) {
        categories.add(item.category);
        if (item.category === 'slabs') hasSlabs = true;
      }
      if (item.condition) conditions.add(item.condition);
      languages.add(item.language || 'english');
      const gc = (item as unknown as { grading_company?: string | null }).grading_company;
      if (gc) gradingCompanies.add(gc);
      const pt = (item as unknown as { sc_product_type?: string | null }).sc_product_type;
      if (pt) productTypes.add(pt);
    }

    return {
      availableGames: Array.from(games),
      availableCategories: Array.from(categories),
      availableConditions: Array.from(conditions),
      availableLanguages: Array.from(languages),
      availableGradingCompanies: Array.from(gradingCompanies),
      availableProductTypes: Array.from(productTypes),
      hasSlabs,
    };
  }, [myItemsRaw, options.includeSold]);

  const clearFilters = () => {
    setActiveTab([]);
    setCategoryFilter([]);
    setConditionFilter('all');
    setPriceMin(0);
    setPriceMax(1000);
    setSelectedGame('all');
    setSelectedLanguages([]);
    setGradingCompany('all');
    setGradeMin(1);
    setGradeMax(10);
    setSearchQuery('');
    setSortBy('newest');
  };

  return {
    activeTab, setActiveTab,
    categoryFilter, setCategoryFilter,
    conditionFilter, setConditionFilter,
    priceMin, setPriceMin,
    priceMax, setPriceMax,
    selectedGame, setSelectedGame,
    selectedLanguages, setSelectedLanguages,
    gradingCompany, setGradingCompany,
    gradeMin, setGradeMin,
    gradeMax, setGradeMax,
    searchQuery, setSearchQuery,
    sortBy, setSortBy,
    filteredItems: displayItems,
    publicListedIds,
    unlistedRawItems,
    unlistedFilteredItems,
    stats,
    itemsWithoutImagesCount,
    pricedItemsCount,
    mobileFilterCount,
    visibilityToggle,
    setVisibilityToggle,
    availableFilterOptions,
    clearFilters,
  };
}
